/**
 * Theme propagation на documentElement (#938).
 *
 * Записывает camelCase поля `config.theme` (primaryColor, primaryHover, …)
 * в соответствующие CSS custom properties (`--theme-primary`, …) inline на
 * `document.documentElement`. Дальше `themeSync.js` через MutationObserver
 * подхватывает изменения и дублирует на shadow hosts (`[data-shiftgears]`) —
 * inline на host'е перебивает `:host{--theme-primary}` defaults из
 * `theme-variables.css`, см. cascade rationale в его header.
 *
 * Раньше эта логика жила inline в `widget.jsx#applyThemeToHost` и вызывалась
 * только из `mount()` (legacy programmatic путь). Auto-init `[data-shiftgears]`
 * путь (`bootstrap.jsx#init()`) её **не вызывал** — поэтому documentElement
 * оставался пустым, `registerHostForThemeSync` копировать было нечего, и
 * виджеты падали в `:host` defaults (синий blue-800 demo-shift-gears) вместо
 * andys красного из `b2bMountParams.theme.primaryColor`.
 */

// camelCase theme keys → `--theme-*` / `--widget-*` / `--radius` CSS
// custom properties. Source of truth — backend WidgetConfig schema +
// theme-variables.css. Widget-side tokens were added so a dealer
// preset (Autodis #965) can darken the embedded widget surface from
// one place instead of inline overrides in every page.
export const THEME_MAPPING = {
  primaryColor: '--theme-primary',
  primaryHover: '--theme-primary-hover',
  accentColor: '--theme-accent',
  bgColor: '--theme-bg-primary',
  textColor: '--theme-text-primary',
  specialOffersColor: '--theme-special-offers',
  fontFamily: '--theme-font-family',
  fontSize: '--theme-font-size-base',
  borderRadius: '--radius',
  cardRadius: '--theme-card-radius',
  buttonRadius: '--theme-button-radius',
  spacingSm: '--theme-spacing-sm',
  spacingMd: '--theme-spacing-md',
  spacingLg: '--theme-spacing-lg',
  // Widget surface — picked up by themeSync.js and pushed onto every
  // registered shadow host (the actual VoiceBar lives there).
  widgetPrimary: '--widget-primary',
  widgetPrimaryHover: '--widget-primary-hover',
  widgetBg: '--widget-bg',
  widgetBgHover: '--widget-bg-hover',
  widgetBgTertiary: '--widget-bg-tertiary',
  widgetText: '--widget-text',
  widgetTextPrimary: '--widget-text-primary',
  widgetTextSecondary: '--widget-text-secondary',
};

/**
 * Записывает theme tokens inline на `document.documentElement`. Безопасно
 * вызывать с пустым/неполным theme (отсутствующие поля пропускаются — не
 * затираем уже установленные значения пустыми). SSR-safe (no-op без document).
 *
 * @param {Record<string, string|number> | null | undefined} theme
 */
export function applyThemeToDocumentElement(theme) {
  if (!theme || typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const [key, value] of Object.entries(theme)) {
    const cssVar = THEME_MAPPING[key];
    if (cssVar && value) {
      root.style.setProperty(cssVar, value);
    }
  }
}

/**
 * Push theme tokens onto documentElement while remembering whatever was
 * there before; returns a `pop()` to restore the snapshot. Used by
 * page-level "this route is a {dealer} surface" hooks that want a clean
 * route-out transition without forcing future routes to re-apply the
 * defaults manually. SSR-safe (returns a no-op pop).
 *
 * @param {Record<string, string|number> | null | undefined} theme
 * @returns {() => void}
 */
export function pushThemeToDocumentElement(theme) {
  if (!theme || typeof document === 'undefined') return () => {};
  const root = document.documentElement;
  const snapshot = {};
  for (const [key, value] of Object.entries(theme)) {
    const cssVar = THEME_MAPPING[key];
    if (!cssVar || !value) continue;
    snapshot[cssVar] = root.style.getPropertyValue(cssVar);
    root.style.setProperty(cssVar, value);
  }
  return () => {
    for (const [cssVar, prev] of Object.entries(snapshot)) {
      if (prev) root.style.setProperty(cssVar, prev);
      else root.style.removeProperty(cssVar);
    }
  };
}
